<script setup lang="ts">
import type { Customer, Job, JobType, Warranty } from '@/types/model'
import { computed, onMounted, ref, watch } from 'vue'
import { type FormDataJob, parseDataJob, sendDataJob } from '@/helpers/job.helper.ts'
import type { ParseData } from '@/types'
import LabelBase from '@/components/label/LabelBase.vue'
import ButtonBase from '@/components/button/ButtonBase.vue'
import { useModalStore } from '@/stores/modal.store.ts'
import { useRouter } from 'vue-router'
import JobInputShow from '@/views/page/job/input/JobInputShow.vue'

// props & emits
const props = defineProps<{
  job: Job
}>()

// router & route
const router = useRouter()

// stores
const { closeModal, showModal } = useModalStore()

// refs
const loaded = ref<boolean>(false)
const sending = ref<boolean>(false)
const data = ref<ParseData<Job> | null>(null)
const jobData = ref<Job>(props.job)

// computed
const typeName = computed(() => {
  return (
    data.value?.jobLists?.job_types?.find((item) => item.value === props.job.job_type)?.label ?? '-'
  )
})
const typeColor = computed(() => {
  return (
    data.value?.jobLists?.job_types?.find((item) => item.value === props.job.job_type)?.color ??
    'stone'
  )
})

// methods
const loadPageData = async () => {
  const parseData = await parseDataJob('create')

  data.value = (parseData as ParseData<Job>) ?? null

  loaded.value = !!data.value
}

const sendData = async () => {
  const job: Job = {
    ...props.job,
  }
  const customer: Customer = {
    name: job.customer_name,
    type: job.customer_type,
    phone: job.phone,
    phone2: job.phone2 ?? undefined,
    phone3: job.phone3 ?? undefined,
    address: job.address,
    email: job.email ?? undefined,
  }
  const warranty: Warranty = {
    model: job.model,
    serial: job.serial,
    purchase_date: job.purchase_date,
    type: job.warranty_type,
  }
  const form: FormDataJob = { job, customer, warranty }

  const err = await sendDataJob('create', form, router)
  if (err) {
    const errMsg = Object.values(err as Record<string, string[]>).join(',') as string
    showModal('error', errMsg)
  }
}

// lifecycle
onMounted(async () => {
  await loadPageData()
})

watch(
  () => typeColor.value,
  (newVal) => {
    // dapatkan data sebelumnya
    if (jobData.value.job_types) {
      const jobTypes: JobType = {
        ...jobData.value.job_types,
      }
      jobTypes.color = newVal

      // masukan ke data
      jobData.value.job_types = jobTypes
    }

    // jika tidak ada buat baru
    jobData.value.job_types = {
      color: newVal,
    }
  },
)

watch(
  () => typeName.value,
  (newVal) => {
    // dapatkan data sebelumnya
    if (jobData.value.job_types) {
      const jobTypes: JobType = {
        ...jobData.value.job_types,
      }
      jobTypes.name = newVal

      // masukan ke data
      jobData.value.job_types = jobTypes
    }

    // jika tidak ada buat baru
    jobData.value.job_types = {
      name: newVal,
    }
  },
)
</script>

<template>
  <div v-if="loaded" class="overflow-y-auto max-h-80">
    <job-input-show :job="jobData"></job-input-show>

    <div
      class="sticky left-0 bottom-0 flex justify-center w-full bg-white dark:bg-stone-800 border-t border-dashed border-stone-200 dark:border-stone-700 rounded-b-xl transition-colors duration-150"
    >
      <div class="grid grid-cols-2 gap-2 p-2">
        <button-base @click="closeModal" variant="secondary" size="sm">Cancel</button-base>
        <button-base @click="sendData" :loading="sending" size="sm">Import Job</button-base>
      </div>
    </div>
  </div>
  <label-base v-else>Sedang memuat data...</label-base>
</template>

<style scoped></style>
